import { useEffect, useState } from "react";
import styled from "styled-components";
import { StepDots } from "./blueShell";
import { Button } from "./shared";
import { colors } from "../theme/colors";

export const QuestionCardWrapper = styled.div`
  position: relative;
  max-width: 55rem;
  margin: 1.5rem auto 0;
`;

export const QuestionCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 1.5rem;
  padding: 2.5rem 3rem 2.5rem 3rem;
  text-align: left;
  color: ${colors.black};
  box-sizing: border-box;

  h2 {
    color: ${colors.navy};
    font-family: 'Jost', sans-serif;
    font-size: 1.375rem;
    margin: 0;
  }

  @media (max-width: 600px) {
    padding: 1.5rem;
  }
`;

// A fixed-size variant of QuestionCard, used wherever the white card must stay
// the same dimensions regardless of content (question reveal, explanation
// slides, legal/security slide decks) instead of sizing to fit its content.
export const FixedQuestionCard = styled(QuestionCard)<{ $transparent?: boolean }>`
  width: 100%;
  height: 42rem;
  display: flex;
  flex-direction: column;
  ${(props) => props.$transparent && "background: transparent;"}
`;

export const CardBody = styled.div`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const QuestionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

export const QuestionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

export const QuestionStepDots = styled(StepDots)`
  justify-content: flex-start;
  margin: 0;
`;

export const TimerBadge = styled.div`
  flex-shrink: 0;
  width: 4rem;
  height: 4rem;
  background-image: url("/images/horloge.png");
  background-size: contain;
  background-repeat: no-repeat;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  line-height: 1;
`;

export const TimerSeconds = styled.span`
  font-size: 0.75rem;
`;

export const TimerLabel = styled.span`
  font-size: 0.5rem;
  text-transform: lowercase;
`;

export const OptionBadge = styled.div`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  background-image: url("/images/Cercle_Lettres.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.navy};
  font-weight: bold;
  font-size: 0.875rem;
`;

export const PrimaryPillButton = styled(Button)`
  background: ${colors.orange};
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 0.75rem 2rem;
  font-weight: bold;

  &:disabled {
    background: #ccc;
  }
`;

export const SlideImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const LegalArrowButton = styled.button<{ $side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.$side === "left" ? "left: 0.5rem;" : "right: 0.5rem;")}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border: none;
  border-radius: 50%;
  background: #ffffff00;
  color: ${colors.orange};
  font-size: 2.75rem;
  line-height: 1;
  cursor: pointer;
`;

const NO_CARD_BACKGROUND_SLIDES = new Set(["conditions-prescriptions.svg"]);

export function SvgSlideDeck({
  slides,
  onSlideChange,
}: {
  slides: string[];
  onSlideChange?: (slide: string) => void;
}) {
  const [index, setIndex] = useState(0);
  const slide = slides[index];

  useEffect(() => {
    onSlideChange?.(slide);
  }, [slide, onSlideChange]);

  return (
    <QuestionCardWrapper>
      <FixedQuestionCard $transparent={NO_CARD_BACKGROUND_SLIDES.has(slide)}>
        <CardBody>
          <SlideImage src={`/images/${encodeURIComponent(slide)}`} alt={slide} />
        </CardBody>
      </FixedQuestionCard>
      {index > 0 && (
        <LegalArrowButton $side="left" onClick={() => setIndex((i) => i - 1)} aria-label="Précédent">
          &lsaquo;
        </LegalArrowButton>
      )}
      {index < slides.length - 1 && (
        <LegalArrowButton $side="right" onClick={() => setIndex((i) => i + 1)} aria-label="Suivant">
          &rsaquo;
        </LegalArrowButton>
      )}
    </QuestionCardWrapper>
  );
}
